import { InjectionToken } from "@angular/core";

/**
 * Runtime configuration for the Zoom Meeting SDK (Client View).
 *
 * Provided in AppModule — see the ZOOM_CONFIG provider there. It no longer
 * carries the SDK key or secret: those are fetched per session from
 * zoom/sdk/credentials/ and decrypted by ZoomCredentialsService, so nothing
 * sensitive is compiled into the bundle. What is left here is the handful of
 * settings that are safe to ship and genuinely static.
 */
export interface ZoomConfig {
  /**
   * Optional backend endpoint returning { signature: string }. When set,
   * ZoomService prefers it and never signs in the browser — the end state that
   * keeps the SDK secret server-side entirely. Example: '/api/zoom/signature'.
   */
  signatureEndpoint?: string;

  /**
   * Meeting SDK version loaded from Zoom's CDN. Zoom enforces a minimum
   * supported version — bump this string as the version nears end of support.
   */
  sdkVersion: string;

  /**
   * Client View requires a leave URL at init(), but nothing navigates to it:
   * ZoomService sets `leaveRedirect: false` so Angular routes every exit itself
   * (see the note on that class). Kept as the fallback Zoom would use if that
   * ever flips back on. A route on this app's own origin, e.g. '/home'.
   */
  leaveUrl: string;
}

export const ZOOM_CONFIG = new InjectionToken<ZoomConfig>("ZOOM_CONFIG");

/** Identity the meeting is joined with. */
export interface ZoomUser {
  name: string;
  email?: string;
}

/**
 * Bridge to the LMS's existing auth. The Zoom page pre-fills the student's
 * name from here.
 */
export abstract class ZoomUserProvider {
  abstract getUser(): ZoomUser | null;
}

/**
 * Reads the CAIRA login session (stored under `caira_session` by
 * CairaAuthService) and pulls out a display name + email.
 *
 * The payload shape varies, so several common field names are tried. If none
 * are present the join page still works — the student types their name on the
 * session card.
 */
export class CairaZoomUserProvider extends ZoomUserProvider {
  getUser(): ZoomUser | null {
    try {
      const raw = localStorage.getItem("caira_session");
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      const name =
        parsed.full_name ||
        parsed.name ||
        parsed.user_name ||
        [parsed.first_name, parsed.last_name].filter(Boolean).join(" ") ||
        [parsed.firstName, parsed.lastName].filter(Boolean).join(" ");
      if (!name) {
        return null;
      }
      return { name, email: parsed.email || parsed.user_email };
    } catch {
      return null;
    }
  }
}
